import React, { useEffect, useState } from "react";
import { selectCid } from "../features/companyid";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { server_API } from "../config";
import { IoMdArrowBack } from "react-icons/io";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";

import { formatDateToShortMonth } from "../utils/dateUtils";

const itemsPerPage = 10;

const providerConfig = {
  hackerrank: {
    title: "HackerRank Applicants",
    scoreLabel: "HackerRank Score",
    candidateField: "hackerrank_candidate_id",
    statusField: "hackerrank_test_status",
    scoreField: "hackerrank_score",
    testIdField: "hackerrank_test_id",
    completionDateField: "attempt_date",
    reportPath: "hackerrank/report",
  },
  coderbyte: {
    title: "Coderbyte Applicants",
    scoreLabel: "Coderbyte Score",
    candidateField: "coderbyte_candidate_id",
    statusField: "coderbyte_test_status",
    scoreField: "coderbyte_score",
    testIdField: "coderbyte_test_id",
    completionDateField: "coderbyte_completed_at",
    reportPath: "coderbyte/report",
  },
};

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const AssessmentCandidatePage = ({ provider }) => {
  const config = providerConfig[provider];
  const pageTitle = config?.title ?? "Applicants";
  const location = useLocation();
  const cid = useSelector(selectCid);
  const navigate = useNavigate();
  const { jid } = location.state;
  const [allApplicants, setAllApplicants] = useState([]);
  const [assessmentCandidates, setAssessmentCandidates] = useState([]);
  const [job, setJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedCandidate, setPaginatedCandidate] = useState([]);

  useEffect(() => {
    axios
      .get(`${server_API}/v2/job/get/${jid}/${cid}`)
      .then((res) => {
        setJob(res.data);
      })
      .catch((err) => {
        console.log(err);
      });

    axios
      .get(`${server_API}/v2/applicant/get/job/${jid}/${cid}`)
      .then((res) => {
        setAllApplicants(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [jid, cid]);

  const showReport = (candidateId) => {
    const testId = job?.[config.testIdField];
    window.open(
      `${server_API}/v2/applicant/${config.reportPath}/${testId}/${candidateId}/${cid}`,
      "_blank",
    );
  };

  useEffect(() => {
    const filteredCandidates = allApplicants.filter((applicant) =>
      hasValue(applicant[config.candidateField]),
    );
    setAssessmentCandidates(filteredCandidates);
    setCurrentPage(1);
  }, [allApplicants, config.candidateField]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const indexOfLastJob = currentPage * itemsPerPage;
  const indexOfFirstJob = indexOfLastJob - itemsPerPage;

  useEffect(() => {
    const indexOfLastCandidate = currentPage * itemsPerPage;
    const indexOfFirstCandidate = indexOfLastCandidate - itemsPerPage;

    if (assessmentCandidates.length > 0) {
      setPaginatedCandidate(
        assessmentCandidates.slice(indexOfFirstCandidate, indexOfLastCandidate),
      );
    } else {
      setPaginatedCandidate([]);
    }
  }, [assessmentCandidates, currentPage]);

  const totalPages = Math.ceil(assessmentCandidates?.length / itemsPerPage);

  const getPageRange = () => {
    const maxVisiblePages = 3;
    const middlePage = currentPage;
    let startPage = middlePage - Math.floor(maxVisiblePages / 2);
    startPage = Math.max(startPage, 1);
    const endPage = Math.min(startPage + maxVisiblePages - 1, totalPages);
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i,
    );
  };

  return (
    <>
      <div className="px-10">
        <div className=" py-5">
          <div className="flex items-center justify-between">
            <p className="text-2xl font-semibold">
              <span>
                <IoMdArrowBack
                  className="text-2xl my-3  tooltip"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  back
                </IoMdArrowBack>
              </span>{" "}
              {pageTitle}
            </p>
          </div>
        </div>
        <div className="p-4  bg-white rounded-xl">
          <div className=" text-sm">
            {paginatedCandidate?.length > 0 ? (
              <div className="mt-5">
                <Table className="border-y">
                  <Thead className=" top-0 z-30 bg-white">
                    <Tr>
                      <Th className="border-y items-center py-5">S. No.</Th>
                      <Th className="border-y items-center  p-2 ">Name</Th>
                      <Th className="border-y items-center  p-2">Email</Th>
                      <Th className="border-y items-center p-2">Status</Th>
                      <Th className="border-y items-center p-2">
                        Completion Date
                      </Th>
                      <Th className="border-y items-center p-2">
                        {config.scoreLabel}
                      </Th>
                      <Th className="border-t-[1px] items-center  p-2">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {paginatedCandidate.map((e, i) => {
                      const testStatus = e[config.statusField];
                      const testScore = e[config.scoreField];
                      const candidateId = e[config.candidateField];
                      const completionDateValue =
                        e[config.completionDateField] || e.attempt_date;
                      return (
                        <Tr className="text-center">
                          <Td className="border-y items-center p-2">
                            {indexOfFirstJob + i + 1}
                          </Td>
                          <Td
                            className="border-y items-center p-2  cursor-pointer text-blue-500 "
                            onClick={() => {
                              navigate("/jobs/candidates/about", {
                                state: { uuid: e.uuid, id: jid },
                              });
                            }}
                          >
                            {e.name}
                          </Td>
                          <Td className="border-y items-center p-2">
                            {e.email}
                          </Td>
                          <Td className="border-y items-center p-2">
                            {testStatus}
                          </Td>
                          <Td className="border-y items-center p-2">
                            {formatDateToShortMonth(completionDateValue) || "-"}
                          </Td>
                          <Td className="border-y items-center p-2">
                            {testScore}
                          </Td>
                          <Td className="border-t-[1px] flex  justify-center items-center  p-2 flex-col">
                            <button
                              className="bg-[#148EFF] whitespace-nowrap text-white py-2 px-4 w-fit rounded-lg"
                              disabled={!candidateId}
                              onClick={() => {
                                showReport(candidateId);
                              }}
                            >
                              View Report
                            </button>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </div>
            ) : (
              <div>No data to be displayed</div>
            )}
          </div>
          <nav className="bg-white px-4 py-6 flex items-center justify-between border-t border-gray-200 sm:px-6 ">
            <div className="hidden sm:block">
              {assessmentCandidates?.length !== 0 ? (
                <p className="text-sm text-gray-700">
                  {indexOfFirstJob + 1} to{" "}
                  {assessmentCandidates?.length < indexOfLastJob
                    ? assessmentCandidates?.length
                    : currentPage !== totalPages
                      ? indexOfLastJob
                      : assessmentCandidates?.length}{" "}
                  of {assessmentCandidates?.length} Applicant
                </p>
              ) : (
                <p className="text-sm text-gray-700">
                  {0} to {0} of {assessmentCandidates?.length} Applicant
                </p>
              )}
            </div>
            <div className="flex justify-between sm:justify-end gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 ${currentPage === 1 ? "opacity-50" : "hover:bg-gray-50"
                  }`}
              >
                Previous
              </button>
              <div className="flex space-x-2">
                {currentPage > 4 && (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 ${currentPage === 1 ? "bg-light-blue text-white" : ""
                        }`}
                    >
                      1
                    </button>
                    <button
                      onClick={() => handlePageChange(2)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 ${currentPage === 2 ? "bg-light-blue text-white" : ""
                        }`}
                    >
                      2
                    </button>
                    <span className="px-2 text-gray-500 text-4xl flex ">
                      ...
                    </span>
                  </>
                )}
                {getPageRange().map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 ${currentPage === page ? "bg-light-blue text-white" : ""
                      }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:text-gray-500 ${currentPage === totalPages ? "opacity-50" : "hover:bg-gray-50"
                  }`}
              >
                Next
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};

export default AssessmentCandidatePage;
